import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Key, User, Check } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/features/auth';
import { useTranslation } from '@/features/i18n';
import { supabase } from '@/lib/supabase';

type ProfileFormData = {
  full_name: string;
  email: string;
};
type PasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export function ProfilePage() {
  const { profile, refreshProfile, updatePassword } = useAuth();
  const [profileSaved, setProfileSaved] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const { t } = useTranslation();

  const profileSchema = z.object({
    full_name: z.string().min(2, t.admin.profile.validation.nameMin),
    email: z.string().email(t.admin.profile.validation.emailInvalid),
  });

  const passwordSchema = z.object({
    currentPassword: z.string().min(1, t.admin.profile.currentPassword),
    newPassword: z.string().min(6, t.admin.profile.validation.passwordMin),
    confirmPassword: z.string(),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t.admin.profile.validation.passwordMatch,
    path: ['confirmPassword'],
  });

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: profile?.full_name || '',
      email: profile?.email || '',
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onProfileSubmit = async (data: ProfileFormData) => {
    if (!profile) return;

    const { error } = await supabase
      .from('profiles')
      .update({ full_name: data.full_name } as any)
      .eq('id', profile.id);

    if (!error) {
      await refreshProfile();
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    const { error } = await updatePassword(data.currentPassword, data.newPassword);
    if (error) {
      passwordForm.setError('currentPassword', { message: error.message });
    } else {
      passwordForm.reset();
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    }
  };

  return (
    <>
      <SEO title={t.admin.profile.title} noIndex />

      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.profile.title}</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t.admin.profile.subtitle}
          </p>
        </div>

        {/* Profile info */}
        <Card>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <User className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <CardTitle>{t.admin.profile.info}</CardTitle>
              <CardDescription>{t.admin.profile.infoDesc}</CardDescription>
            </div>
          </div>

          <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
            <Input
              label={t.admin.profile.fullName}
              error={profileForm.formState.errors.full_name?.message}
              {...profileForm.register('full_name')}
            />
            <Input
              label={t.admin.profile.email}
              type="email"
              disabled
              hint={t.admin.profile.emailHint}
              {...profileForm.register('email')}
            />
            <div className="flex items-center gap-4">
              <Button
                type="submit"
                isLoading={profileForm.formState.isSubmitting}
                leftIcon={profileSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              >
                {profileSaved ? t.admin.profile.saved : t.admin.profile.saveChanges}
              </Button>
              {profile?.role && (
                <span className="text-sm text-neutral-500">
                  {t.admin.profile.role}: {profile.role === 'admin' ? t.admin.profile.roleAdmin : t.admin.profile.roleEditor}
                </span>
              )}
            </div>
          </form>
        </Card>

        {/* Change password */}
        <Card>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <Key className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <CardTitle>{t.admin.profile.changePassword}</CardTitle>
              <CardDescription>{t.admin.profile.changePasswordDesc}</CardDescription>
            </div>
          </div>

          <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
            <Input
              label={t.admin.profile.currentPassword}
              type="password"
              error={passwordForm.formState.errors.currentPassword?.message}
              {...passwordForm.register('currentPassword')}
            />
            <Input
              label={t.admin.profile.newPassword}
              type="password"
              error={passwordForm.formState.errors.newPassword?.message}
              {...passwordForm.register('newPassword')}
            />
            <Input
              label={t.admin.profile.confirmPassword}
              type="password"
              error={passwordForm.formState.errors.confirmPassword?.message}
              {...passwordForm.register('confirmPassword')}
            />
            <Button
              type="submit"
              isLoading={passwordForm.formState.isSubmitting}
              leftIcon={passwordSaved ? <Check className="w-4 h-4" /> : <Key className="w-4 h-4" />}
            >
              {passwordSaved ? t.admin.profile.passwordChanged : t.admin.profile.changePassword}
            </Button>
          </form>
        </Card>
      </div>
    </>
  );
}
